import React, { useState, useEffect, useCallback } from 'react';
import BitrixAPI from '../api/b24';
import { usePermissions } from '../hooks/usePermissions';
import { useHeader } from '../contexts/HeaderContext';
import { API_URL } from '../config';

// ── Справочники ────────────────────────────────────────────────────────────
const EXPENSE_TYPES = {
  790: 'Стекло',
  791: 'Фурнитура для душевых',
  801: 'Фурнитура LOFT',
  792: 'Производственные расходники',
  793: 'Материалы для матовки',
  794: 'Канцтовары',
  795: 'Услуги закалки',
  796: 'Транспортные услуги',
  797: 'Услуги подрядчиков',
  798: 'Операционные расходы',
  799: 'Заправка топлива',
  800: 'Хозяйственные расходы',
  802: 'Аванс сотруднику',
  803: 'Зарплата',
  804: 'Упаковочные материалы',
  805: 'Ремонт оборудования',
  806: 'Инструменты',
  807: 'IT расходы',
};

const PRIORITIES = {
  808: 'Нормальный',
  809: 'Средний',
  810: 'Высокий',
  811: 'Срочный',
};

const PRIORITY_COLORS = {
  808: 'bg-gray-100 text-gray-600',
  809: 'bg-yellow-100 text-yellow-700',
  810: 'bg-orange-100 text-orange-700',
  811: 'bg-red-100 text-red-700',
};

const STAGES = BitrixAPI.BUH_STAGES;

const STAGE_META = {
  [STAGES.NEW]:        { label: 'Новый запрос',        badge: 'bg-blue-100 text-blue-700',    left: 'border-l-blue-400' },
  [STAGES.APPROVED]:   { label: 'Утверждено',           badge: 'bg-green-100 text-green-700',  left: 'border-l-green-400' },
  [STAGES.IN_PAYMENT]: { label: 'Ожидает оплату',        badge: 'bg-purple-100 text-purple-700',left: 'border-l-purple-400' },
  [STAGES.PAID]:       { label: 'Оплачено',             badge: 'bg-teal-100 text-teal-700',    left: 'border-l-teal-400' },
  [STAGES.DONE]:       { label: 'Заказ принят',         badge: 'bg-emerald-100 text-emerald-700',left: 'border-l-emerald-400' },
  [STAGES.REJECTED]:   { label: 'Отклонено',            badge: 'bg-red-100 text-red-700',      left: 'border-l-red-400' },
};

// ── Роли ───────────────────────────────────────────────────────────────────
const ROLE_OWNER     = 'owner';
const ROLE_DIRECTOR  = 'director';
const ROLE_BUH       = 'buhgalter';
const ROLE_INITIATOR = 'initiator';

function getBuhRole(user) {
  const r = user?.role || '';
  if (['admin', 'adminb24'].includes(r)) return ROLE_OWNER;
  if (r === 'director')                  return ROLE_DIRECTOR;
  if (r === 'buhgalter')                 return ROLE_BUH;
  return ROLE_INITIATOR;
}

function getVisibleStages(role) {
  if (role === ROLE_OWNER)    return Object.values(STAGES);
  if (role === ROLE_DIRECTOR) return [STAGES.NEW, STAGES.APPROVED, STAGES.IN_PAYMENT, STAGES.PAID, STAGES.REJECTED, STAGES.DONE];
  if (role === ROLE_BUH)      return [STAGES.APPROVED, STAGES.IN_PAYMENT, STAGES.PAID, STAGES.DONE];
  return [STAGES.NEW, STAGES.APPROVED, STAGES.IN_PAYMENT, STAGES.PAID, STAGES.DONE, STAGES.REJECTED];
}

function canCreate(role) {
  return role === ROLE_INITIATOR || role === ROLE_OWNER || role === ROLE_DIRECTOR;
}

// ── Форматирование ─────────────────────────────────────────────────────────
function formatAmount(amount, currency = 'KZT') {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('ru-KZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Полноэкранный просмотр файла ───────────────────────────────────────────
function FileFullscreen({ file, proxyUrl, onClose }) {
  const ext = (file.name || '').split('.').pop().toLowerCase();
  const isKnownNonImage = ['pdf', 'docx', 'doc', 'xls', 'xlsx', 'zip', 'rar', '7z'].includes(ext);
  const [imgFailed, setImgFailed] = useState(false);
  const useImageTag = !isKnownNonImage && !imgFailed;
  const iframeRef = React.useRef();

  const downloadUrl = `${proxyUrl}&download=1&filename=${encodeURIComponent(file.name || 'file')}`;

  const handlePrint = () => {
    if (useImageTag) {
      const win = window.open('', '_blank', 'width=900,height=700');
      if (!win) return;
      win.document.write(`<!DOCTYPE html><html><head><title>${file.name}</title>
        <style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#fff}
        img{max-width:100%;max-height:100vh;object-fit:contain}</style></head>
        <body><img src="${proxyUrl}"></body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); }, 600);
    } else {
      iframeRef.current?.contentWindow?.print();
    }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/80 flex flex-col"
      style={{ zIndex: 1200 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Шапка */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 flex-shrink-0">
        <span className="text-white text-sm font-medium truncate flex-1 min-w-0">{file.name}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handlePrint}
            title="Печать"
            className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-colors text-base"
          >🖨</button>
          <a
            href={downloadUrl}
            download={file.name || 'file'}
            title="Скачать"
            className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-colors text-base"
          >⬇</a>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-xl text-2xl transition-colors"
          >×</button>
        </div>
      </div>

      {/* Контент */}
      <div className="flex-1 overflow-hidden flex items-center justify-center px-4 pb-4">
        {useImageTag ? (
          <img
            src={proxyUrl}
            alt={file.name}
            className="max-w-full max-h-full object-contain rounded-xl"
            style={{ maxHeight: 'calc(100vh - 80px)' }}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <iframe
            ref={iframeRef}
            src={proxyUrl}
            title={file.name}
            className="rounded-xl bg-white"
            style={{ width: 'min(860px, 100%)', height: 'calc(100vh - 80px)' }}
          />
        )}
      </div>
    </div>
  );
}

// ── Файловая карточка (мини) + полноэкранный просмотр ─────────────────────
function FileLink({ file }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  if (!file) return null;

  if (!file.url) {
    return <span className="text-sm text-gray-500 flex items-center gap-1.5"><span>📎</span>{file.name}</span>;
  }

  const token = localStorage.getItem('token');
  const proxyUrl = `${API_URL}/api/buh-file?url=${encodeURIComponent(file.url)}&token=${token}`;

  const ext = (file.name || '').split('.').pop().toLowerCase();
  const isDownloadOnly = ['zip', 'rar', '7z', 'doc'].includes(ext);
  const isPdf = ext === 'pdf';
  const isDocx = ext === 'docx';
  const isExcel = ['xls', 'xlsx'].includes(ext);
  const isKnownNonImage = isDownloadOnly || isPdf || isDocx || isExcel;

  const cardIcon = isPdf ? '📄' : isDocx ? '📝' : isExcel ? '📊' : '📎';
  const cardLabel = isPdf ? 'PDF' : isDocx ? 'Word' : isExcel ? 'Excel'
    : ext && ext.length <= 5 ? ext.toUpperCase() : 'Файл';

  if (isDownloadOnly) {
    return (
      <a href={proxyUrl} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 bg-gray-50 border border-gray-200 hover:border-blue-300 rounded-xl px-3 py-2 transition-colors">
        <span className="text-xl">📎</span>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
          <p className="text-xs text-gray-400">{ext.toUpperCase()} · скачать</p>
        </div>
      </a>
    );
  }

  return (
    <>
      <button
        onClick={() => setFullscreen(true)}
        className="w-full flex items-center gap-3 bg-gray-50 border border-gray-200 hover:border-blue-400 hover:bg-blue-50 rounded-xl px-3 py-2.5 transition-colors text-left group"
      >
        {/* Миниатюра: пробуем img для всех типов кроме заведомо не-изображений */}
        <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center">
          {!isKnownNonImage && !thumbError ? (
            <img
              src={proxyUrl}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setThumbError(true)}
            />
          ) : (
            <span className="text-2xl">{cardIcon}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-800 truncate">{file.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">{cardLabel} · нажмите для просмотра</p>
        </div>
        <span className="text-gray-300 group-hover:text-blue-400 text-lg flex-shrink-0 transition-colors">⛶</span>
      </button>

      {fullscreen && (
        <FileFullscreen file={file} proxyUrl={proxyUrl} onClose={() => setFullscreen(false)} />
      )}
    </>
  );
}

// ── Работа с файлами ───────────────────────────────────────────────────────
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const fetchBitrixFileAsBase64 = async (fileUrl, fileName) => {
  const token = localStorage.getItem('token');
  const proxyUrl = `${API_URL}/api/buh-file?url=${encodeURIComponent(fileUrl)}&token=${token}`;
  const response = await fetch(proxyUrl);
  if (!response.ok) throw new Error(`Не удалось скачать файл: ${fileName}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const FILE_ICONS = { pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊' };

function FilePreviewCard({ name, size, preview, onRemove }) {
  const ext = name.split('.').pop().toLowerCase();
  const icon = FILE_ICONS[ext] || '📎';
  const sizeLabel = size < 1024 * 1024
    ? `${(size / 1024).toFixed(0)} КБ`
    : `${(size / 1024 / 1024).toFixed(1)} МБ`;

  return (
    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-2">
      <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-white border border-gray-100 flex items-center justify-center text-xl">
        {preview
          ? <img src={preview} alt={name} className="w-full h-full object-cover" />
          : <span>{icon}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-800 truncate">{name}</p>
        <p className="text-xs text-gray-400">{sizeLabel}</p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors text-base leading-none">
        ×
      </button>
    </div>
  );
}

// ── Бейджи ─────────────────────────────────────────────────────────────────
function StageBadge({ stageId }) {
  const meta = STAGE_META[stageId] || { label: stageId, badge: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${meta.badge}`}>{meta.label}</span>;
}

function PriorityBadge({ value }) {
  if (!value || value === 808) return null;
  const color = PRIORITY_COLORS[value] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${color}`}>{PRIORITIES[value] || value}</span>;
}

// ── Строка детали в модальном окне ─────────────────────────────────────────
function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-800">{value}</span>
    </div>
  );
}

// ── Модальное окно: детали заявки ──────────────────────────────────────────
function DetailModal({ item, role, onClose, onApprove, onReject, onInPayment, onPaid, onDone, onSaved, processing }) {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentOrderFile, setPaymentOrderFile] = useState(null);
  const paymentFileRef = React.useRef();
  const isProcessing = processing.has(item.id);

  // ── Редактируемые поля (только бухгалтер/владелец) ─────────────────────
  const canEditFields = role === ROLE_BUH || role === ROLE_OWNER;
  const [newPoaFiles, setNewPoaFiles] = useState([]);
  const [newPaymentFiles, setNewPaymentFiles] = useState([]);
  const [edoLinkValue, setEdoLinkValue] = useState(item.edoLink || '');
  const [fieldsSaving, setFieldsSaving] = useState(false);
  const [fieldsError, setFieldsError] = useState('');
  const poaFileInputRef = React.useRef();
  const paymentFileInputRef = React.useRef();

  const handleAddFiles = async (e, setter) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const items = await Promise.all(files.map(async f => {
      const base64 = await fileToBase64(f);
      return { name: f.name, base64, size: f.size };
    }));
    setter(prev => [...prev, ...items]);
    e.target.value = '';
  };

  const handleSaveFields = async () => {
    setFieldsSaving(true);
    setFieldsError('');
    try {
      const fields = {};
      if (newPaymentFiles.length) {
        const reUploaded = await Promise.all(
          (item.paymentOrderFiles || []).filter(f => f.url).map(async f => {
            const base64 = await fetchBitrixFileAsBase64(f.url, f.name);
            return [f.name, base64];
          })
        );
        fields[BitrixAPI.BUH_FIELDS.PAYMENT_ORDER_FILE] = [
          ...reUploaded,
          ...newPaymentFiles.map(f => [f.name, f.base64]),
        ];
      }
      if (newPoaFiles.length) {
        const reUploaded = await Promise.all(
          (item.poaFiles || []).filter(f => f.url).map(async f => {
            const base64 = await fetchBitrixFileAsBase64(f.url, f.name);
            return [f.name, base64];
          })
        );
        fields[BitrixAPI.BUH_FIELDS.POA_FILE] = [
          ...reUploaded,
          ...newPoaFiles.map(f => [f.name, f.base64]),
        ];
      }
      if (edoLinkValue !== (item.edoLink || ''))
        fields[BitrixAPI.BUH_FIELDS.EDO_LINK] = edoLinkValue;

      if (Object.keys(fields).length === 0) return;

      await BitrixAPI.updateBuhItem(item.id, fields);
      setNewPoaFiles([]);
      setNewPaymentFiles([]);
      const freshItem = await BitrixAPI.getBuhItemById(item.id);
      if (onSaved) onSaved(item.id, freshItem);
    } catch (err) {
      setFieldsError('Ошибка сохранения: ' + err.message);
    } finally {
      setFieldsSaving(false);
    }
  };

  const fieldsDirty = newPoaFiles.length > 0 || newPaymentFiles.length > 0 || edoLinkValue !== (item.edoLink || '');

  const handlePaymentFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setPaymentOrderFile({ name: file.name, base64 });
    e.target.value = '';
  };

  const handlePaymentConfirm = () => {
    const files = paymentOrderFile ? [[paymentOrderFile.name, paymentOrderFile.base64]] : null;
    onPaid(item.id, files);
    setShowPaymentForm(false);
    setPaymentOrderFile(null);
    onClose();
  };

  const canApprove   = (role === ROLE_OWNER || role === ROLE_DIRECTOR) && item.stageId === STAGES.NEW;
  const canReject    = (role === ROLE_OWNER || role === ROLE_DIRECTOR) && item.stageId === STAGES.NEW;
  const canInPayment = (role === ROLE_OWNER || role === ROLE_BUH)      && item.stageId === STAGES.APPROVED;
  const canPaid      = (role === ROLE_OWNER || role === ROLE_BUH)      && item.stageId === STAGES.IN_PAYMENT;
  const canDone      = (role === ROLE_OWNER || role === ROLE_BUH)      && item.stageId === STAGES.PAID;
  const hasActions   = canApprove || canReject || canInPayment || canPaid || canDone;

  // Закрытие по Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 px-3 pb-3 sm:px-0 sm:pb-0" style={{ zIndex: 1100 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      <div className="bg-white w-full sm:max-w-lg rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: 'calc(100dvh - 16px)', height: 'auto' }}>

        {/* Шапка — sticky */}
        <div className="flex items-start justify-between gap-3 px-5 pt-3 pb-3 sm:pt-5 sm:pb-4 border-b flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <StageBadge stageId={item.stageId} />
              <PriorityBadge value={item.priority} />
            </div>
            <h2 className="text-base font-bold text-gray-900 leading-snug">#{item.id} {item.title}</h2>
            <div className="text-2xl font-bold text-gray-800 mt-1">{formatAmount(item.opportunity, item.currencyId)}</div>
          </div>
          <button onClick={onClose}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 text-xl">
            ×
          </button>
        </div>

        {/* Тело — скроллится */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <DetailRow label="Тип расхода" value={EXPENSE_TYPES[item.expenseType] || null} />
            <DetailRow label="Инициатор" value={item.initiator} />
            <DetailRow label="Создан" value={formatDate(item.createdTime)} />
            <DetailRow label="Связанные заказы" value={item.relatedOrders} />
            <DetailRow label="Кто принял товар" value={item.whoReceived} />
          </div>

          {item.comment && (
            <div className="bg-gray-50 rounded-xl p-3">
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1">Комментарий</span>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.comment}</p>
            </div>
          )}

          {item.rejectionReason?.trim() && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <span className="text-xs text-red-400 uppercase tracking-wide block mb-1">Причина отклонения</span>
              <p className="text-sm text-red-700">{item.rejectionReason.trim()}</p>
            </div>
          )}

          {/* Счёт и спецификация — только просмотр */}
          {(item.invoiceFiles || []).length > 0 && (
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">Счёт на оплату</span>
              <div className="space-y-2">{item.invoiceFiles.map(f => <FileLink key={f.id} file={f} />)}</div>
            </div>
          )}
          {(item.specFiles || []).length > 0 && (
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">Спецификация</span>
              <div className="space-y-2">{item.specFiles.map(f => <FileLink key={f.id} file={f} />)}</div>
            </div>
          )}

          {/* ── Редактируемые поля ─────────────────────────────────────────── */}
          <div className="border-t pt-4 space-y-4">

            {/* Платёжное поручение */}
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                Платёжное поручение / платёжка из банка
              </span>
              {(item.paymentOrderFiles || []).length > 0
                ? <div className="space-y-2 mb-2">{item.paymentOrderFiles.map(f => <FileLink key={f.id} file={f} />)}</div>
                : <p className="text-sm text-gray-400 italic mb-2">не заполнено</p>
              }
              {canEditFields && (
                <>
                  <input ref={paymentFileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                    onChange={e => handleAddFiles(e, setNewPaymentFiles)} className="hidden" />
                  {newPaymentFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-3 py-1.5 mb-1">
                      <span className="flex-1 text-xs text-purple-700 truncate">📎 {f.name}</span>
                      <button onClick={() => setNewPaymentFiles(p => p.filter((_, idx) => idx !== i))}
                        className="text-purple-400 hover:text-red-500 text-base">×</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => paymentFileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-1.5 border border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 text-gray-400 hover:text-purple-600 rounded-xl py-2 text-xs transition-colors">
                    + Прикрепить файл
                  </button>
                </>
              )}
            </div>

            {/* Доверенность */}
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                Доверенность (файл)
              </span>
              {(item.poaFiles || []).length > 0
                ? <div className="space-y-2 mb-2">{item.poaFiles.map(f => <FileLink key={f.id} file={f} />)}</div>
                : <p className="text-sm text-gray-400 italic mb-2">не заполнено</p>
              }
              {canEditFields && (
                <>
                  <input ref={poaFileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                    onChange={e => handleAddFiles(e, setNewPoaFiles)} className="hidden" />
                  {newPoaFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-1.5 mb-1">
                      <span className="flex-1 text-xs text-blue-700 truncate">📎 {f.name}</span>
                      <button onClick={() => setNewPoaFiles(p => p.filter((_, idx) => idx !== i))}
                        className="text-blue-400 hover:text-red-500 text-base">×</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => poaFileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-1.5 border border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-xl py-2 text-xs transition-colors">
                    + Прикрепить файл
                  </button>
                </>
              )}
            </div>

            {/* ЭДО ссылка */}
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wide block mb-1.5">
                Доверенность Учет ЭДО (ссылка на документ)
              </span>
              {canEditFields ? (
                <div>
                  <input
                    type="url"
                    value={edoLinkValue}
                    onChange={e => setEdoLinkValue(e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
              ) : edoLinkValue ? (
                <a href={edoLinkValue} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline break-all">{edoLinkValue}</a>
              ) : (
                <p className="text-sm text-gray-400 italic">не заполнено</p>
              )}
            </div>

            {/* Кнопка сохранения */}
            {canEditFields && fieldsDirty && (
              <>
                {fieldsError && <p className="text-xs text-red-500">{fieldsError}</p>}
                <button
                  onClick={handleSaveFields}
                  disabled={fieldsSaving}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                  {fieldsSaving ? 'Сохранение...' : '💾 Сохранить изменения'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Кнопки действий — sticky внизу */}
        {hasActions && (
          <div className="px-5 pb-5 pt-3 border-t flex-shrink-0 space-y-2">
            {showRejectForm ? (
              <>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Укажите причину отклонения..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { onReject(item.id, rejectReason); setShowRejectForm(false); setRejectReason(''); onClose(); }}
                    disabled={isProcessing}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    {isProcessing ? '...' : 'Подтвердить'}
                  </button>
                  <button onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 px-4 rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </>
            ) : showPaymentForm ? (
              <>
                <span className="text-xs text-gray-500 uppercase tracking-wide block">Платежный ордер</span>
                <input
                  ref={paymentFileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  onChange={handlePaymentFileSelect}
                  className="hidden"
                />
                {paymentOrderFile ? (
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                    <span className="flex-1 text-sm text-gray-700 truncate">📎 {paymentOrderFile.name}</span>
                    <button type="button" onClick={() => setPaymentOrderFile(null)}
                      className="text-gray-400 hover:text-red-500 text-lg leading-none">×</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => paymentFileRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-teal-400 hover:bg-teal-50 text-gray-500 hover:text-teal-600 rounded-xl py-2.5 text-sm transition-colors">
                    <span>📎</span> Прикрепить ордер (необязательно)
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handlePaymentConfirm}
                    disabled={isProcessing}
                    className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    {isProcessing ? '...' : '✔ Подтвердить оплату'}
                  </button>
                  <button onClick={() => { setShowPaymentForm(false); setPaymentOrderFile(null); }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 px-4 rounded-xl transition-colors">
                    Отмена
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap gap-2">
                {canApprove && (
                  <button onClick={() => { onApprove(item.id); onClose(); }} disabled={isProcessing}
                    className="flex-1 min-w-28 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    {isProcessing ? '...' : '✅ Утвердить'}
                  </button>
                )}
                {canReject && (
                  <button onClick={() => setShowRejectForm(true)} disabled={isProcessing}
                    className="flex-1 min-w-28 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    ❌ Отклонить
                  </button>
                )}
                {canInPayment && (
                  <button onClick={() => { onInPayment(item.id); onClose(); }} disabled={isProcessing}
                    className="flex-1 min-w-36 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    {isProcessing ? '...' : '💳 На оплату'}
                  </button>
                )}
                {canPaid && (
                  <button onClick={() => setShowPaymentForm(true)} disabled={isProcessing}
                    className="flex-1 min-w-28 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    ✔ Оплачено
                  </button>
                )}
                {canDone && (
                  <button onClick={() => { onDone(item.id); onClose(); }} disabled={isProcessing}
                    className="flex-1 min-w-28 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-xl transition-colors">
                    {isProcessing ? '...' : '🏁 Принят'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Минималистичная карточка заявки ────────────────────────────────────────
function RequestCard({ item, onClick }) {
  const meta = STAGE_META[item.stageId] || { left: 'border-l-gray-300' };
  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white border border-gray-200 border-l-4 ${meta.left} rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-shadow active:scale-[0.99]`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <PriorityBadge value={item.priority} />
            {item.expenseType && (
              <span className="text-xs text-gray-500 truncate">{EXPENSE_TYPES[item.expenseType]}</span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-900 truncate">#{item.id} {item.title}</p>
          {item.initiator && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">👤 {item.initiator}</p>
          )}
          {item.rejectionReason?.trim() && (
            <p className="text-xs text-red-500 mt-0.5 truncate">⛔ {item.rejectionReason.trim()}</p>
          )}
        </div>
        <div className="flex-shrink-0 text-right">
          <div className="text-base font-bold text-gray-800 whitespace-nowrap">
            {formatAmount(item.opportunity, item.currencyId)}
          </div>
          <div className="text-xs text-gray-400 mt-0.5 whitespace-nowrap">
            {item.createdTime ? new Date(item.createdTime).toLocaleDateString('ru-RU') : ''}
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Модальное окно создания заявки ─────────────────────────────────────────
function CreateRequestModal({ onClose, onCreated, currentUser }) {
  const initiatorName = currentUser?.username || currentUser?.name || '';
  const [form, setForm] = useState({
    title: '',
    amount: '',
    expenseType: '',
    priority: '808',
    comment: '',
    relatedOrders: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = React.useRef();

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const items = await Promise.all(files.map(async (file) => {
      const base64 = await fileToBase64(file);
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      return { file, base64, preview };
    }));
    setAttachments(prev => [...prev, ...items]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => {
      const next = [...prev];
      if (next[idx].preview) URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim())                        { setError('Укажите название заявки'); return; }
    if (!form.amount || isNaN(Number(form.amount))){ setError('Укажите корректную сумму'); return; }
    if (!form.expenseType)                         { setError('Выберите тип расхода'); return; }
    setError('');
    setSaving(true);
    try {
      const invoiceFiles = attachments.map(a => [a.file.name, a.base64]);
      const result = await BitrixAPI.createBuhItem({
        title: form.title.trim(),
        amount: Number(form.amount),
        expenseType: form.expenseType ? Number(form.expenseType) : null,
        priority: form.priority ? Number(form.priority) : null,
        comment: form.comment,
        initiator: initiatorName,
        relatedOrders: form.relatedOrders,
        invoiceFiles,
      });
      onCreated(result.item);
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Закрытие по Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 px-3 pb-3 sm:px-0 sm:pb-0" style={{ zIndex: 1100 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      <div
        className="bg-white w-full sm:max-w-lg rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: 'calc(100dvh - 16px)' }}>

        {/* Шапка */}
        <div className="flex items-center justify-between px-5 pt-3 pb-3 sm:pt-5 sm:pb-4 border-b flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">Новая заявка на оплату</h2>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 text-xl">
            ×
          </button>
        </div>

        {/* Форма — скроллится */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>
          )}

          {/* Инициатор — только чтение */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Инициатор</label>
            <div className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-600">
              {initiatorName || '—'}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Название заявки *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="Напр.: Оплата поставщику ООО Стекло"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Сумма (KZT) *</label>
            <input
              type="number"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder="0"
              min="0"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Тип расхода *</label>
            <select
              value={form.expenseType}
              onChange={e => set('expenseType', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
              <option value="">— Выберите тип —</option>
              {Object.entries(EXPENSE_TYPES).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Приоритет</label>
            <select
              value={form.priority}
              onChange={e => set('priority', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
              {Object.entries(PRIORITIES).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Связанные заказы</label>
            <input
              type="text"
              value={form.relatedOrders}
              onChange={e => set('relatedOrders', e.target.value)}
              placeholder="Номера заказов через запятую"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Комментарий</label>
            <textarea
              value={form.comment}
              onChange={e => set('comment', e.target.value)}
              placeholder="Пояснения к заявке..."
              rows={3}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
            />
          </div>

          {/* Вложения */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Счёт на оплату / файлы</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="hidden"
            />
            {attachments.length > 0 && (
              <div className="space-y-2 mb-2">
                {attachments.map((a, i) => (
                  <FilePreviewCard
                    key={i}
                    name={a.file.name}
                    size={a.file.size}
                    preview={a.preview}
                    onRemove={() => removeAttachment(i)}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600 rounded-xl py-3 text-sm transition-colors">
              <span className="text-lg">📎</span>
              Прикрепить файл
            </button>
          </div>
        </form>

        {/* Кнопки — sticky внизу */}
        <div className="px-5 pb-5 pt-3 border-t flex gap-3 flex-shrink-0">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 rounded-xl transition-colors text-sm">
            {saving ? 'Создание...' : 'Создать заявку'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-5 rounded-xl transition-colors text-sm">
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Главная страница ────────────────────────────────────────────────────────
export default function BuhgalteriyaPage() {
  const { user } = usePermissions();
  const { setHeaderExtras } = useHeader();
  const role = getBuhRole(user);
  const visibleStages = getVisibleStages(role);

  const [activeTab, setActiveTab]         = useState(visibleStages[0] || STAGES.NEW);
  const [allItems, setAllItems]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [processing, setProcessing]       = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [detailItem, setDetailItem]       = useState(null);
  const [searchTerm, setSearchTerm]       = useState('');

  const loadItems = useCallback(async () => {
    try {
      setError(null);
      const items = await BitrixAPI.getBuhItems(visibleStages);
      setAllItems(items);
    } catch (err) {
      setError('Ошибка загрузки: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [visibleStages.join(',')]); // eslint-disable-line

  useEffect(() => {
    loadItems();
    const interval = setInterval(loadItems, 60000);
    return () => clearInterval(interval);
  }, [loadItems]);

  useEffect(() => { setHeaderExtras(null); }, [setHeaderExtras]);

  // ── Фильтрация ─────────────────────────────────────────────────────────
  const currentUserName = (user?.username || user?.name || '').toLowerCase();

  const itemsByStage = {};
  visibleStages.forEach(s => { itemsByStage[s] = []; });
  allItems.forEach(item => {
    if (!itemsByStage[item.stageId]) return;
    // Инициатор видит только свои заявки
    if (role === ROLE_INITIATOR && item.initiator.toLowerCase() !== currentUserName) return;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const match =
        item.title.toLowerCase().includes(q) ||
        String(item.id).includes(q) ||
        item.initiator.toLowerCase().includes(q) ||
        String(item.relatedOrders || '').toLowerCase().includes(q);
      if (!match) return;
    }
    itemsByStage[item.stageId].push(item);
  });

  const activeItems = itemsByStage[activeTab] || [];
  const totalAmount = activeItems.reduce((sum, i) => sum + (Number(i.opportunity) || 0), 0);

  // ── Действия ───────────────────────────────────────────────────────────
  const markProcessing   = (id) => setProcessing(prev => new Set([...prev, id]));
  const unmarkProcessing = (id) => setProcessing(prev => { const s = new Set(prev); s.delete(id); return s; });

  const moveStage = async (id, stageId, extraFields = {}) => {
    markProcessing(id);
    try {
      await BitrixAPI.updateBuhItemStage(id, stageId, extraFields);
      setAllItems(prev => prev.map(i => i.id === id ? { ...i, stageId, ...extraFields } : i));
    } catch (err) {
      setError('Ошибка: ' + err.message);
    } finally {
      unmarkProcessing(id);
    }
  };

  const handleSaved = (id, freshItem) => {
    setAllItems(prev => prev.map(i => i.id === id ? freshItem : i));
    setDetailItem(freshItem);
  };

  const handleApprove   = (id) => moveStage(id, STAGES.APPROVED);
  const handleReject    = (id, reason) => moveStage(id, STAGES.REJECTED, { [BitrixAPI.BUH_FIELDS.REJECTION_REASON]: reason });
  const handleInPayment = (id) => moveStage(id, STAGES.IN_PAYMENT);
  const handlePaid      = (id, paymentOrderFiles) => {
    const extra = {};
    if (paymentOrderFiles?.length) extra[BitrixAPI.BUH_FIELDS.PAYMENT_ORDER_FILE] = paymentOrderFiles;
    moveStage(id, STAGES.PAID, extra);
  };
  const handleDone      = (id) => moveStage(id, STAGES.DONE);

  const handleCreated = (item) => {
    setAllItems(prev => [item, ...prev]);
    setShowCreateModal(false);
    setActiveTab(STAGES.NEW);
  };

  // ── Рендер ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Верхняя панель: поиск + обновление */}
      <div className="px-4 pt-4 pb-2 flex gap-2 items-center bg-white border-b sticky top-0 z-10">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Поиск по ID, названию, инициатору..."
          className="flex-1 border border-gray-200 bg-gray-50 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <button
          onClick={loadItems}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-500 transition-colors"
          title="Обновить">
          🔄
        </button>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-700">×</button>
        </div>
      )}

      {/* Вкладки */}
      <div className="px-4 pt-3 pb-2 flex overflow-x-auto gap-1.5 flex-shrink-0"
        style={{ scrollbarWidth: 'none' }}>
        {visibleStages.map(stageId => {
          const meta = STAGE_META[stageId] || { label: stageId };
          const count = itemsByStage[stageId]?.length || 0;
          const isActive = activeTab === stageId;
          return (
            <button
              key={stageId}
              onClick={() => setActiveTab(stageId)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {meta.label}
              {count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  isActive ? 'bg-white/30 text-white' : 'bg-gray-300 text-gray-700'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Итог строки */}
      {activeItems.length > 0 && (
        <div className="px-4 pb-1 text-xs text-gray-400">
          {activeItems.length} заявок · Итого: <strong className="text-gray-600">{formatAmount(totalAmount)}</strong>
        </div>
      )}

      {/* Список — скроллится */}
      <div className="flex-1 overflow-y-auto px-4 pb-24">
        {activeItems.length === 0 ? (
          <div className="text-center py-16 text-gray-300">
            <div className="text-5xl mb-3">📋</div>
            <p className="text-sm font-medium text-gray-400">
              {searchTerm ? 'Ничего не найдено' : (STAGE_META[activeTab]?.label || '') + ' — пусто'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 pt-2">
            {activeItems.map(item => (
              <RequestCard
                key={item.id}
                item={item}
                onClick={() => setDetailItem(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* FAB — кнопка + */}
      {canCreate(role) && (
        <button
          onClick={() => setShowCreateModal(true)}
          className="fixed bottom-6 right-5 w-14 h-14 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white rounded-full shadow-lg flex items-center justify-center text-3xl transition-all" style={{ zIndex: 1050 }}>
          +
        </button>
      )}

      {/* Модальное окно деталей */}
      {detailItem && (
        <DetailModal
          item={detailItem}
          role={role}
          onClose={() => setDetailItem(null)}
          processing={processing}
          onApprove={handleApprove}
          onReject={handleReject}
          onInPayment={handleInPayment}
          onPaid={handlePaid}
          onDone={handleDone}
          onSaved={handleSaved}
        />
      )}

      {/* Модальное окно создания */}
      {showCreateModal && (
        <CreateRequestModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
          currentUser={user}
        />
      )}
    </div>
  );
}
